import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import { NavLink, Switch, Route, Redirect } from "react-router-dom";

import { gradients, GridWrapper, Banner, FromTopPage } from "../theme";
import Layout from "../components/annexes/AnnexesLayout";

const Header = styled(Banner)`
  margin-bottom: 65px;

  @media (max-width: 1023px) {
    margin-bottom: 45px;
  }

  > div {
    display: block;
    padding: 112px 0 0;

    @media (max-width: 767px) {
      padding-top: 50px;
    }
  }

  h1 {
    font-size: 4.8rem;
    font-weight: 500;
    letter-spacing: -0.1rem;
    margin-bottom: 52px;
    text-transform: none;

    @media (max-width: 767px) {
      margin-bottom: 30px;
    }
  }

  nav {
    display: flex;
    width: 100%;

    @media (max-width: 767px) {
      display: block;
    }
  }

  a {
    border-left: 4px solid white;
    color: white;
    display: inline-block;
    font-size: 1.4rem;
    font-weight: 600;
    height: 61px;
    letter-spacing: 0.02rem;
    padding-left: 10px;
    position: relative;
    text-decoration: none;
    transition: 0.3s;
    width: calc(100% / 4 - 10px);

    :before {
      background: ${gradients.purpleToPink("to bottom")};
      content: "";
      height: 0;
      left: -4px;
      position: absolute;
      top: 0;
      transition: 0.3s;
      width: 4px;
    }

    :hover,
    &.active {
      margin-top: -10px;
      height: 71px;

      :before {
        height: 90px;
      }
    }

    @media (max-width: 1023px) {
      text-align: left;
    }

    @media (max-width: 767px) {
      display: block;
      height: auto;
      padding: 12px 12px 24px;
      width: 100%;

      :hover,
      &.active {
        margin: 0;
        height: auto;

        :before {
          height: 100%;
        }
      }
    }
  }
`;

function Jobs() {
  return (
    <FromTopPage>
      <Helmet>
        <title>Recrutement</title>
        <meta
          key="og:title"
          property="og:title"
          content="Recrutement | Le Média"
        />
        ,
      </Helmet>
      <Header>
        <GridWrapper>
          <h1>Recrutement</h1>
          <nav>
            <NavLink to="/recrutement/stage-enquete">
              Stage pôle Enquête
            </NavLink>
          </nav>
        </GridWrapper>
      </Header>
      <Switch>
        <Route path="/recrutement/stage-enquete">
          <Layout title="Stage pôle Enquête">
            <section>
              <h3>Qui sommes-nous ?</h3>
              <p>
                Depuis 8 ans, Le Média s’engage à porter une information libre,
                indépendante et affranchie des pressions financières et
                industrielles. Fidèle à son manifeste, Le Média est un projet
                citoyen, coopératif et alternatif : il associe sociétaires,
                salarié·e·s et bénéficiaires dans sa gouvernance et s’appuie sur
                des collaborations multiples (journalistes, correspondant·e·s,
                associations, citoyen·ne·s).
              </p>
              <p>
                Notre ligne éditoriale est progressiste, humaniste, écologiste,
                antiraciste, antisexiste et féministe. Nous défendons une
                information qui laisse place à la confrontation des idées et aux
                débats de fond, loin de la dictature de l’urgence.
              </p>
              <p>
                Nous diffusons nos enquêtes, débats et reportages sur YouTube,
                Molotov, la Freebox (canal 165) et bientôt, nous l’espérons, sur
                la TNT en Île de France. Notre mission : construire un espace
                commun, pluraliste et accessible, au service du débat
                démocratique et des solidarités humaines.
              </p>
              <p>
                En collaboration directe avec la rédaction, et sous la
                responsabilité de la cheffe de service et du rédacteur en chef,
                vous aurez pour mission d’aider à concevoir, programmer et
                valoriser les enquêtes du Média.
              </p>
              <h3>Vos missions</h3>
              <h4>Programmation éditoriale</h4>
              <ul>
                <li>
                  Participation à la réflexion éditoriale avec la cheffe de
                  pôle: déterminer si un sujet est pertinent, quel angle
                  adopter, sous quel format le traiter (15’, 26’ ou secret
                  d’instruction), et quels interlocuteurs approcher (experts,
                  témoins, institutions, etc.). Vous discuterez de la
                  faisabilité du projet et de sa mise en œuvre (Pourquoi ?
                  Comment ? Qui le porte ?)
                </li>
                <li>
                  Organisation du calendrier de production (les différentes
                  deadline)
                  <ul>
                    <li>Périodes de recherches</li>
                    <li>Semaines de tournage</li>
                    <li>Planning de dérushage et de montage</li>
                  </ul>
                </li>
                <li>
                  Suivi de la coordination avec les pigistes et techniciens.
                </li>
              </ul>
              <h4>Recherches documentaires</h4>
              <ul>
                <li>
                  Vous serez amené·e à faire un travail de recherche important
                  en OSINT (recherche en sources ouvertes) pour rassembler des
                  informations fiables.
                </li>
                <li>
                  Lecture et fichage de sources diverses : articles, rapports
                  publics, décisions de justice, documents administratifs,
                  ouvrages spécialisés, etc.
                </li>
                <li>
                  Vérification de faits (fact-checking) : tout élément diffusé
                  doit être sourcé et vérifiable.
                </li>
                <li>
                  Attention : ne jamais prendre pour acquis ce qu’un témoin
                  affirme sans validation par croisement des sources.
                </li>
              </ul>
              <h4>Prise de contact avec les intervenants</h4>
              <ul>
                <li>
                  Dans le cadre des enquêtes, vous serez amené·e à contacter des
                  témoins, des experts ou des personnes impliquées dans les
                  sujets traités. 
                </li>
                <li>
                  Présentation claire des enjeux de l’enquête, des objectifs
                  souhaités et de leur rôle/l’utilité de leur témoignage. 
                </li>
                <li>
                  Canaux utilisés :
                  <ul>
                    <li>
                      Email professionnel (en mettant en copie les journalistes
                      en charge de l’enquête)
                    </li>
                    <li>
                      Téléphone, SMS, WhatsApp, Telegram, voire Signal dans
                      certains cas.
                    </li>
                  </ul>
                </li>
              </ul>
              <h4>Préparation des interviews</h4>
              <ul>
                <li>
                  Possibilité de réaliser des pré-interviews pour préparer les
                  intervenants aux tournages ou aux plateaux d’émission.
                </li>
                <li>Participation à la rédaction des questions à poser.</li>
                <li>
                  Préparation de fiches intervenants : éléments biographiques,
                  contexte, points sensibles à aborder.
                </li>
              </ul>
              <h4>Participation aux tournages</h4>
              <p>
                Vous accompagnerez les journalistes sur le terrain lors de
                certains tournages. Vous pourrez vous déplacer en Île-de-France
                en transports en commun, ou ailleurs en France si une voiture de
                location est prévue.
              </p>
              <ul>
                <li>Assistance terrain aux journalistes et JRI.</li>
                <li>
                  Aide logistique (installation du matériel, prise de notes,
                  propositions d’images).
                </li>
                <li>
                  Participation active à la captation (en fonction des besoins).
                </li>
                <li>
                  Observation et apprentissage in situ des pratiques
                  journalistiques de terrain.
                </li>
              </ul>
              <h4>Dérushage, transcription</h4>
              <p>Dérushage systématique après chaque tournage :</p>
              <ul>
                <li>
                  Création d’un document partagé listant les rushs disponibles:
                  bilan précis de tout le matériel tourné, en classant les
                  images et en identifiant les moments-clés.
                </li>
                <li>
                  Transcriptions complètes des interviews, avec noms des
                  intervenants et timecodes (avec l’aide de Première Pro).
                </li>
              </ul>
              <p>
                Ce travail est essentiel, car il permet aux journalistes de
                retrouver rapidement les éléments pertinents, et au monteur de
                se repérer efficacement dans les rushs.
              </p>
              <h4>Rédaction du script</h4>
              <p>
                Le script est le document écrit qui sert de trame à la vidéo :
                il doit être le plus détaillé que possible et inclure les voix
                off, les séquences sonores (avec les TC), les citations, et
                organiser l’ensemble de manière fluide et structurée. Vous
                pourrez y contribuer activement, en proposant des formulations,
                en structurant les séquences, et en apportant un regard
                extérieur sur la narration.
              </p>
              <ul>
                <li>
                  Le script doit être le plus détaillé possible, et fidèle aux
                  faits et sources.
                </li>
                <li>
                  Vos propositions sont les bienvenues ! Soyez force de
                  suggestion.
                </li>
              </ul>
              <h4>Montage</h4>
              <p>Le montage prend plusieurs jours, selon le format :</p>
              <ul>
                <li>
                  Pour les formats courts “Le 15’” et “Secrets d’instruction”,
                  il faut compter entre 2 et 4 jours de montage selon la
                  complexité du sujet.
                </li>
                <li>
                  Pour un format long “Le 26’”, le montage dure en moyenne 10
                  jours.
                </li>
              </ul>
              <p>
                Vous serez amené·e à assister à ces journées de montage, en
                fonction du rythme de travail du monteur. En général, le montage
                se fait en binôme : un·e monteur·euse et le/ la journaliste en
                charge de l’enquête travaillent côte à côte pour structurer le
                reportage.
              </p>
              <ul>
                <li>Aide à l’identification des séquences pertinentes.</li>
                <li>
                  Proposition d’habillages graphiques, d’illustrations
                  visuelles, de musiques d’ambiance ou de transitions.
                </li>
                <li>Recherches d’images d’archives, de JT etc…</li>
                <li>Aucune compétence préalable en montage n’est exigée !</li>
              </ul>
              <p>
                Ce sera l’occasion de voir concrètement comment une enquête
                prend forme à l’image, et de comprendre les choix narratifs et
                esthétiques qui la façonnent.
              </p>

              <h4>Veille et nouvelles idées d’enquête</h4>
              <ul>
                <li>
                  Veille régulière sur l’actualité, les sujets émergents, les
                  signaux faibles.
                </li>
                <li>
                  Proposition de sujets potentiels à traiter : construction de
                  notes d’intention ou premiers éléments d’angle.
                </li>
              </ul>
              <h4>Communication autour des publications</h4>
              <p>Rédaction de (selon les besoins) :</p>
              <ul>
                <li>La description YouTube des vidéos.</li>
                <li>Une newsletter de publication.</li>
                <li>
                  De courts textes pour les réseaux sociaux (en lien avec
                  l’équipe RS).
                </li>
              </ul>
              <p>
                L’objectif est de valoriser l’enquête et de mobiliser le public
                autour du sujet.
              </p>

              {/* <h3>Profil recherché</h3>
              <ul>
                <li>Formation en journalisme, communication ou équivalent.</li>
                <li>
                  Solide connaissance de l’actualité et intérêt marqué pour les
                  écritures web et sociales.
                </li>
                <li>
                  Bonne maîtrise de la suite Adobe (Premiere Pro indispensable ;
                  Photoshop apprécié).
                </li>
                <li>
                  Maîtrise des codes narratifs et visuels propres aux réseaux
                  sociaux.
                </li>
                <li>Très bonne plume et aisance orale face caméra.</li>
                <li>Créativité, curiosité, réactivité.</li>
                <li>Esprit d’équipe et polyvalence indispensables.</li>
              </ul>

              <h3>Conditions</h3>
              <ul>
                <li>Poste à temps plein — 35h.</li>
                <li>
                  Rémunération selon la Convention collective nationale des
                  journalistes (Presse nationale).
                </li>
              </ul> */}
              <h3>Contact</h3>
              <p>
                Merci d'envoyer CV et lettre de motivation à{" "}
                <a href="mailto:emploi@lemediatv.fr">emploi@lemediatv.fr</a>
              </p>
            </section>
          </Layout>
        </Route>
        <Redirect to="/recrutement/stage-enquete" />
      </Switch>
    </FromTopPage>
  );
}

export default Jobs;
